import { IResult } from 'ua-parser-js';
import { FixedOptions } from '../page-data/fixed-option-data';
export enum SFFProblemType {
  ModuleIntro = 'P0',
  Type1 = 'P1',
  Type2 = 'P2',
  Type3 = 'P3',
  Type4 = 'P4',
  Type5 = 'P5',
  Type6 = 'P6',
  Type7 = 'P7',
  Discourse = 'Discourse',
  DiscourseFixed = 'DiscourseFixed',
  SelfEval = 'SelfEval',
  PeerEval = 'PeerEval',
  SkillCompletion = 'SkillCompletion',
  ModuleCompletion = 'ModuleCompletion',
  TagAnnotation = 'TagAnnotation',
  AnnotateSingle = 'AnnotateSingle',
  AnnotateMulti = 'AnnotateMulti',
  FixedOption = 'FixedOption',
}

const inputTypes = [
  SFFProblemType.Type1,
  SFFProblemType.Type2,
  SFFProblemType.Type3,
  SFFProblemType.Type4,
  SFFProblemType.Type7,
];
const selectTypes = [SFFProblemType.Type5];
const constructTypes = [SFFProblemType.Type6];
const introTypes = [SFFProblemType.ModuleIntro];
const fixedOptionTypes = [SFFProblemType.FixedOption];
const vestigialTypes = [
  SFFProblemType.TagAnnotation,
  SFFProblemType.AnnotateSingle,
  SFFProblemType.AnnotateMulti,
];

const evalTypes = [SFFProblemType.SelfEval, SFFProblemType.PeerEval];

export const isInputType = (t: SFFProblemType): boolean =>
  inputTypes.indexOf(t) >= 0;
export const isSelectType = (t: SFFProblemType): boolean =>
  selectTypes.indexOf(t) >= 0;
export const isConstructType = (t: SFFProblemType): boolean =>
  constructTypes.indexOf(t) >= 0;
export const isIntroType = (t: SFFProblemType): boolean =>
  introTypes.indexOf(t) >= 0;
export const isFixedOptionType = (t: SFFProblemType): boolean =>
  fixedOptionTypes.indexOf(t) >= 0;
export const isSkillCompletion = (t: SFFProblemType): boolean =>
  t === SFFProblemType.SkillCompletion;
export const isModuleCompletion = (t: SFFProblemType): boolean =>
  t === SFFProblemType.ModuleCompletion;
export const isDiscourse = (t: SFFProblemType): boolean =>
  t === SFFProblemType.Discourse;
export const isVestigialType = (t: SFFProblemType): boolean =>
  vestigialTypes.indexOf(t) >= 0;
export const isEvalType = (t: SFFProblemType): boolean =>
  evalTypes.indexOf(t) >= 0;

  
  export type ProblemOptionId = string;

export interface TagText {
  imperative: string;
  declarative: string;
  gerund: string;
}

export interface PatientInfo {
  name: string;
  background: string;
}

export interface PatientData {
  patient: PatientInfo;
  difficulty: number;
}
export type ScenarioID = string;

export interface Content {
  text?: string;
  markdown?: string;
}

export interface Scenario {
  id: ScenarioID;
  metadata: PatientData;
  content: Content;
  name: string;
}

export interface Point {
  path: [number, number];
  offset: number;
}

export interface Range {
  anchor: Point;
  focus: Point;
}
export interface DiscourseAnnotation {
  id: string;
  anchor: Point;
  focus: Point;
  tag: string;
}

export type DiscourseAnswers = Record<string, string>;

type AnnotationId = string;
type DiscourseOption = string;

/**
 * For each annotation, where user must select tag that best matches, what are the 'tags' that are
 * available as the options.
 */
export type DiscourseOptions = Record<AnnotationId, DiscourseOption[]>;

export interface SelectOption {
  correct: boolean;
  text: TagText;
  quality: number;
}

export interface SelectOptionMap {
  [key: string]: SelectOption;
}

export interface RawSelectStatement {
  id: string;
  text: string;
  quality: number;
  options: SelectOptionMap;
}

export interface RawSelectStatementMap {
  [key: string]: RawSelectStatement;
}

export interface HintExplanation {
  text?: string;
  markdown?: string;
}

export interface HintsDefinition {
  id: string;
  explanation: HintExplanation;
  reason: TagText;
  quality: number;
  name: string;
}

export type Hints = HintsDefinition[] | undefined;

export interface ProblemData {
  markdown: string;
}

export interface Curriculum {
  semanticVersion: string;
  name: string;
}

export interface SkillContent {
  text?: string;
  markdown?: string;
}

export interface Skill {
  id: string;
  content: SkillContent;
  name: string;
}

export enum ITSEventType {
  sessionStart = 'session start',
  problemLoad = 'problem load',
  firstTryWrong = 'first try wrong',
  problemSubmit = 'problem submit',
  reviewClick = 'review click',
  selectOption = 'select option',
  openTips = 'open tips',
  problemResults = 'problem results',
  evaluationStartTime = 'therapist evaluation start time',
  evaluationEndTime = 'therapist evaluation time',
  evaluationPause = 'therapist evaluation pause time',
  reevaluationStartTime = 'self reevaluation start time',
  reevaluationEndTime = 'self reevaluation time',
  reevaluationPause = 'self reevaluation pause time',
  postCompletionProblemReview = 'post completion problem review',
  bugReport = 'bug report',
}

export interface SelectOptionEventData {
  id: ProblemOptionId;
  selected: boolean;
  correctOption: boolean;
  tags: TagData[];
}

export interface SelectAnnotationOptionEventData {
  annotId: string;
  selectedTagId: ProblemOptionId;
  selected: boolean;
  annotTagId: string;
}

export interface ReviewClickEventData {
  id: ProblemOptionId;
}

export interface Time {
  time: number;
}

export type ITSEventData =
  | SelectOptionEventData
  | ReviewClickEventData
  | RawPageData
  | SelectAnnotationOptionEventData
  | Time;

export interface ITSEvent {
  eventType: ITSEventType;
  eventData?: ITSEventData;
  clientTime: number;
}

export interface ItsLoggerEvent extends ITSEvent {
  uid: string;
  cid: string;
  activityId: string;
  sessionId: string;
  timestamp?: number;
  id?: string;
}

export interface TagResult {
  tagId: string;
  name: string;
  correct: boolean;
}

export interface PlayStats {
  userId: string;
  sessionId: string;
  level: number;
  progress?: number;
  cumulativeTime: number;
  score: number;
  events: ITSEvent[];
  tagResults: TagResult[];
}

export enum BugType {
  technical = 'technical',
  typo = 'typo',
  disagree = 'disagree with answer',
  ambiguous = 'ambiguous',
  other = 'other',
}

export interface BugData {
  issueType?: BugType;
  issueDescription: string;
  email?: string;
  platformInfo: IResult;
}

export interface RawPageData {
  problemQuality?: boolean;
  problemType?: string;
  question?: string | TagText;
  seed: number;
  type: SFFProblemType;
  skill: Skill;
  ignoreList: string[];
  scenario?: Scenario;
  playStats?: PlayStats;
  bugData?: BugData;
}

export interface Explanation {
  text?: string;
  markdown?: string;
}

export interface Tag {
  id: string;
  name: string;
  reason: TagText;
  explanation: Explanation;
  skill: string;
  /** Positive number for positive trait, negative number for negative trait */
  quality: number;
}

export type Tags = Record<string, Tag>;

export enum TagIds {
  linkBehavior = `Link behavioral health symptom to external experience`,
  restateExperience = `Restate patient's affective experience or report`,
  restateIssues = `Restate life context issues, problems, or stressors`
}

export type LastElement = Record<'last', boolean>
export interface DiscourseLine {
  type: string;
  children: Array<{ text: string }>;
  element?: LastElement;
}

export type DisourceCustomFeedbackResponse = {
  id: string;
  module: string;
  annotations: DiscourseCustomFeedback[];
}

export type DiscourseCustomFeedback = {
  id: string; // id for the annotation
  tagIdToFeedback: { [tagId: string]: string };
}

export interface RawDiscoursePageData extends RawPageData {
  id: string; /** Main id of this question, found in the json definitions of the questions */
  content: DiscourseLine[];
  annotations: DiscourseAnnotation[];
  tags: Tags;
  scenario: Scenario;
  answers?: DiscourseAnswers;
  // List of options that the user picks their answer from
  annotationOptions?: DiscourseOptions;
  // Mapping containing custom review feedback that better explains why a tag is or is not applicable
  // to an annotation
  annotationIdToCustomFeedback?: DiscourseCustomFeedback[];
}

export interface FixedOptionInfo {
  tagId: string;
  fixedOptions: FixedOptions;
  question: string;
  answer: string;
  reason: string;
}
export interface RawDiscourseFixedPageData extends RawDiscoursePageData {
  fixedOptionInfo: FixedOptionInfo;
}

export enum EvalOption {
  Correct = 'Correct',
  PartiallyCorrect = 'Partially Correct',
  Incorrect = 'Incorrect',
}

export type EvalAnswers = Record<string, EvalOption>;

export interface TagAvoid extends Tag {
  avoidReason: string;
}

export type TagsAvoid = Record<string, TagAvoid>;
export interface RawEvalPageData extends RawDiscoursePageData {
  evalAnswers: EvalAnswers;
  firstResponse?: string;
  secondResponse?: string;
  tagsToAvoid: TagsAvoid;
  customQuestionPrompt?: string;
}
export interface RawInputPageData extends RawPageData {
  problemQuality: boolean;
  problemType: string;
  options: ResponseDefinition[];
  hints?: HintsDefinition[];
}

export interface RawIntroPageData extends RawPageData {
  data: ProblemData;
}

export interface RawFixedOption {
  name: string;
  selected: boolean;
}

export type RawFixedOptions = Record<string, RawFixedOption>;
export interface RawFixedOptionPageData extends RawPageData {
  options: RawFixedOptions;
  exemplarId: string;
  answer: string;
  reason: string;
  tagId: string;
  question: string;
}

export interface RawSkillCompletionPageData extends RawPageData {
  nextSkill: Skill;
}

export interface RawModuleCompletionPageData extends RawIntroPageData {
  moduleSkillList?: string[];
}

export enum PageType {
  Problem = 'problem',
  Banner = 'banner',
}

export type GetProblemFailureReasons = 'Insufficient options';
export function getProblemFailureReason(
  response: WithStatus
): GetProblemFailureReasons | undefined {
  return response ? response.reason : undefined;
}

export declare type Status = 'Success' | 'Failed';
export interface WithStatus {
  status: Status;
  reason?: GetProblemFailureReasons;
}
export function getStatus(response: WithStatus): Status {
  return response.status;
}

export interface TagData {
  id: string;
  name: string;
}
export interface ResponseDefinition {
  correct: boolean;
  id: ProblemOptionId;
  text?: TagText | string;
  content?: Content;
  reason: TagText[];
  quality: number;
  metadata?: object;
  name?: string;
  selected?: boolean;
  tagData: TagData[];
  /**
   * review feedback for this response that is custom tailored to context, show this
   * to the user on incorrect response in addition to or instead of generic statements
   */
  customReviewFeedback?: string;
}

export interface Level {
  main: number;
  secondary: number;
}
export interface ProblemDefinition extends WithStatus {
  skill: Skill;
  quality: boolean;
  question?: string;
  problemType: string;
  options?: ResponseDefinition[];
  seed: number;
  type: SFFProblemType;
  scenario?: Scenario;
  statements?: RawSelectStatementMap;
  instruction?: string;
  hints?: HintsDefinition[];
  data?: ProblemData;
  level?: Level;
  ignoreList: string[];
}

export const convertResponseToRawPageData = (
  problemDefinition: ProblemDefinition
): RawPageData => {
  const { quality } = problemDefinition;

  const problem: RawPageData = {
    ...problemDefinition,
    problemQuality: quality,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete (problem as any).quality;

  return problem;
};
